import { Divider, IconButton, Typography } from '@material-ui/core';
import { DeleteOutline, Lock } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import {
  Color,
  Stack,
  useResponsiveValue,
  VisibilityObserver,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { Sidebar } from 'shared/layout/Sidebar';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { SidebarMenuItemLink } from 'shared/routing/SidebarMenuItemLink';
import { useFlag } from 'shared/settings/FeatureToggles';
import { useSavedReportsList } from '../data/ReportsAPI';
import { ReportsCustomDTO } from '../data/ReportsDTO';
import { ReportsSidebarMenuItemLink } from './ReportsSidebarMenuItemLink';
import { ReportDeleteMenu } from './ReportDeleteMenu';
import { usePermissionAccess } from 'shared/modules/permission/data/usePermissionAccess';
import { UpgradePlanDialog } from 'carrier/loads/core/UpgradePlanDialog';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';
import { ReportsDashboard, trackReportsEvent } from '../data/ReportsAnalytics';

const REPORTS_MENU_ITEMS: Array<{
  label: string;
  to: string;
  dashboard: ReportsDashboard;
}> = [
  {
    label: 'Revenue',
    to: '/reports/revenue/',
    dashboard: 'revenue',
  },
  {
    label: 'Expenses',
    to: '/reports/expenses/',
    dashboard: 'expenses',
  },
  {
    label: 'Delivery Trend',
    to: '/reports/delivery-trend/',
    dashboard: 'delivery_trend',
  },
  {
    label: 'Invoices',
    to: '/reports/invoices/',
    dashboard: 'invoices',
  },
];

export function ReportsSidebar() {
  const isMobile = useResponsiveValue(true, false);
  const isReportingEnabled = useFlag('ctms_reporting');
  const { isOpenSubscriptionCancelledDialog } =
    useShowSubscriptionCancelledDialog();
  const { data: settings, isLoading: isSettingsLoading } = useCarrierSettings();
  const [showPlanUpdate, setShowPlanUpdate] = useState(false);
  const [deleteAnchorRef, setDeleteAnchorRef] = useState<HTMLElement | null>(
    null,
  );
  const [savedReportToDelete, setSavedReportToDelete] =
    useState<ReportsCustomDTO>();
  const match = useMatch('/reports/custom-report/*');
  const location = useLocation();
  const navigate = useNavigate();
  const { data, fetchNextPage, isLoading, hasNextPage } = useSavedReportsList();
  const savedReports = useMemo(
    () => data?.pages.flatMap((page) => page.data),
    [data],
  );

  const hasNotManageAccess = usePermissionAccess({
    name: 'reports',
    permission: 'can_manage',
  });

  const shouldShowUpgradePlanDialog =
    isSettingsLoading || isOpenSubscriptionCancelledDialog
      ? false
      : !settings?.can_open_reporting;

  useEffect(() => {
    const state = location.state as {
      showUpgradePlanDialog?: boolean;
      restrictedDashboard?: ReportsDashboard | null;
    } | null;

    if (state?.showUpgradePlanDialog) {
      setShowPlanUpdate(true);
      trackReportsEvent({
        name: 'Carrier Opened Reporting Upgrade Dialog',
        dashboard: state.restrictedDashboard ?? null,
        trigger: 'restricted_page',
      });
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
    }
  }, [location, navigate]);

  return (
    <Sidebar title={isReportingEnabled ? 'Reporting' : 'Reports'}>
      <Stack space="small">
        {isReportingEnabled && (
          <div>
            <ReportsSidebarMenuItemLink to="/reports/overview/">
              Overview
            </ReportsSidebarMenuItemLink>
            {REPORTS_MENU_ITEMS.map((item) => (
              <ReportsSidebarMenuItemLink
                key={item.label}
                to={item.to}
                action={
                  shouldShowUpgradePlanDialog && (
                    <Lock htmlColor={Color.Dark100} fontSize="small" />
                  )
                }
                onClick={(event) => {
                  if (shouldShowUpgradePlanDialog) {
                    event.preventDefault();
                    setShowPlanUpdate(true);
                    trackReportsEvent({
                      name: 'Carrier Opened Reporting Upgrade Dialog',
                      dashboard: item.dashboard,
                      trigger: 'sidebar_menu',
                    });
                  }
                }}
              >
                {item.label}
              </ReportsSidebarMenuItemLink>
            ))}
          </div>
        )}

        <div>
          {isReportingEnabled && (
            <Box marginLeft="medium" marginTop="xsmall" marginBottom="xsmall">
              <TextBox color="secondary" variant="body-semibold">
                OTHER REPORTS
              </TextBox>
            </Box>
          )}

          <SidebarMenuItemLink to="/reports/accounts-receivable/">
            Accounts Receivable
          </SidebarMenuItemLink>
          <SidebarMenuItemLink to="/reports/company-revenue/">
            Company Revenue
          </SidebarMenuItemLink>
          <SidebarMenuItemLink to="/reports/accounts-payable/">
            Accounts Payable
          </SidebarMenuItemLink>
          <SidebarMenuItemLink to="/reports/driver-pay/">
            Driver Pay Report
          </SidebarMenuItemLink>
          <SidebarMenuItemLink to="/reports/custom-report/" selected={!!match}>
            Custom Report
          </SidebarMenuItemLink>
        </div>

        {(!!match || isMobile) && (
          <>
            <Divider variant="middle" />

            <Stack space="xsmall">
              <Box marginLeft="medium" marginTop="xsmall">
                <TextBox color="secondary" variant="body-semibold">
                  SAVED REPORTS
                </TextBox>
              </Box>

              {savedReports?.map((report: ReportsCustomDTO) => {
                return (
                  <SidebarMenuItemLink
                    key={report.guid}
                    to={`/reports/custom-report/${report.guid}`}
                    action={
                      !hasNotManageAccess && (
                        <IconButton
                          aria-label={`saved-report-${report.guid}`}
                          onClick={(e) => {
                            setSavedReportToDelete(report);
                            setDeleteAnchorRef(e.currentTarget);
                          }}
                        >
                          <DeleteOutline
                            htmlColor={Color.Dark100}
                            fontSize="small"
                          />
                        </IconButton>
                      )
                    }
                  >
                    {report.name}
                  </SidebarMenuItemLink>
                );
              })}

              {savedReportToDelete && (
                <ReportDeleteMenu
                  deleteAnchorRef={deleteAnchorRef}
                  open={!!savedReportToDelete}
                  savedReport={savedReportToDelete}
                  onCancel={() => {
                    setSavedReportToDelete(undefined);
                    setDeleteAnchorRef(null);
                  }}
                  onDeleteSuccess={() => {
                    setSavedReportToDelete(undefined);
                    setDeleteAnchorRef(null);
                  }}
                  onClose={() => {
                    setDeleteAnchorRef(null);
                    setSavedReportToDelete(undefined);
                  }}
                />
              )}

              {!savedReports?.length && !isLoading && (
                <Box marginLeft="medium">
                  <Typography color="textSecondary">
                    Click Save on the top right to save your first report
                  </Typography>
                </Box>
              )}

              {!isLoading && hasNextPage && !!savedReports?.length && (
                <VisibilityObserver
                  onChange={(visibility) => {
                    if (visibility === 'visible') {
                      void fetchNextPage();
                    }
                  }}
                  render={({ ref }) => (
                    <div ref={ref}>
                      <Stack>
                        <Skeleton animation="wave" />
                        <Skeleton animation="wave" />
                      </Stack>
                    </div>
                  )}
                />
              )}
            </Stack>
          </>
        )}
      </Stack>

      <UpgradePlanDialog
        onClose={() => {
          setShowPlanUpdate(false);
        }}
        open={showPlanUpdate && !isOpenSubscriptionCancelledDialog}
        type="reports"
        source="Reports"
      />
    </Sidebar>
  );
}
